'use client';

import { cn } from '@/lib/cn';
import type { ConsumerSocialPublishPlatform } from '@/types/social-publishing';

type BrandIconProps = {
  className?: string;
};

function InstagramBrandIcon({ className }: BrandIconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id="social-instagram-gradient" x1="3" y1="21" x2="21" y2="3" gradientUnits="userSpaceOnUse">
          <stop stopColor="#FFDC80" />
          <stop offset="0.32" stopColor="#F77737" />
          <stop offset="0.68" stopColor="#E1306C" />
          <stop offset="1" stopColor="#833AB4" />
        </linearGradient>
      </defs>
      <rect x="2" y="2" width="20" height="20" rx="6" fill="url(#social-instagram-gradient)" />
      <rect x="6.5" y="6.5" width="11" height="11" rx="3.25" stroke="white" strokeWidth="1.8" />
      <circle cx="12" cy="12" r="2.6" stroke="white" strokeWidth="1.8" />
      <circle cx="16.7" cy="7.4" r="1" fill="white" />
    </svg>
  );
}

function TikTokBrandIcon({ className }: BrandIconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M14.35 3.1v10.35a3.65 3.65 0 1 1-3.65-3.65c.38 0 .75.06 1.1.17v2.9a1.1 1.1 0 1 0-.2 2.18 1.1 1.1 0 0 0 1.1-1.1V3.1h2.55c.28 1.38 1.08 2.54 2.3 3.35.59.4 1.25.68 1.95.82v2.7a7.72 7.72 0 0 1-5.15-2.28v5.76a6.2 6.2 0 1 1-6.2-6.2c.38 0 .75.03 1.1.1"
        stroke="#25F4EE"
        strokeWidth="2.1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14.35 3.1v10.35a3.65 3.65 0 1 1-3.65-3.65c.38 0 .75.06 1.1.17v2.9a1.1 1.1 0 1 0-.2 2.18 1.1 1.1 0 0 0 1.1-1.1V3.1h2.55c.28 1.38 1.08 2.54 2.3 3.35.59.4 1.25.68 1.95.82v2.7a7.72 7.72 0 0 1-5.15-2.28v5.76a6.2 6.2 0 1 1-6.2-6.2c.38 0 .75.03 1.1.1"
        stroke="#FE2C55"
        strokeWidth="2.1"
        strokeLinecap="round"
        strokeLinejoin="round"
        transform="translate(-0.75 0.45)"
      />
      <path
        d="M14.35 3.1v10.35a3.65 3.65 0 1 1-3.65-3.65c.38 0 .75.06 1.1.17v2.9a1.1 1.1 0 1 0-.2 2.18 1.1 1.1 0 0 0 1.1-1.1V3.1h2.55c.28 1.38 1.08 2.54 2.3 3.35.59.4 1.25.68 1.95.82v2.7a7.72 7.72 0 0 1-5.15-2.28v5.76a6.2 6.2 0 1 1-6.2-6.2c.38 0 .75.03 1.1.1"
        stroke="#111111"
        strokeWidth="1.65"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function YouTubeBrandIcon({ className }: BrandIconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M23.5 6.2a3.02 3.02 0 0 0-2.12-2.13C19.51 3.55 12 3.55 12 3.55s-7.51 0-9.38.52A3.02 3.02 0 0 0 .5 6.2 31.3 31.3 0 0 0 0 12a31.3 31.3 0 0 0 .5 5.8 3.02 3.02 0 0 0 2.12 2.13c1.87.52 9.38.52 9.38.52s7.51 0 9.38-.52a3.02 3.02 0 0 0 2.12-2.13A31.3 31.3 0 0 0 24 12a31.3 31.3 0 0 0-.5-5.8Z"
        fill="#FF0000"
      />
      <path d="m9.75 15.5 6.5-3.5-6.5-3.5v7Z" fill="white" />
    </svg>
  );
}

function XBrandIcon({ className }: BrandIconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.657l-5.214-6.817-5.982 6.817H1.666l7.73-8.835L1.012 2.25h6.826l4.713 6.231 5.693-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644Z" />
    </svg>
  );
}

const ICONS = {
  instagram: InstagramBrandIcon,
  tiktok: TikTokBrandIcon,
  youtube: YouTubeBrandIcon,
  x: XBrandIcon,
} as const;

const TONES: Record<ConsumerSocialPublishPlatform, string> = {
  instagram: 'bg-[#F6F0FF]',
  tiktok: 'bg-[#EEF2F6] text-[#111111]',
  youtube: 'bg-[#FFF0F0]',
  x: 'bg-[#EEF2F6] text-[#050505]',
};

export function SocialPlatformIcon({
  platform,
  className,
  iconClassName,
}: {
  platform: ConsumerSocialPublishPlatform;
  className?: string;
  iconClassName?: string;
}) {
  const Icon = ICONS[platform];
  return (
    <span className={cn('inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px]', TONES[platform], className)}>
      <Icon className={cn('h-5 w-5', iconClassName)} />
    </span>
  );
}
